'use client'

import { useResendTimer } from '@/core/hooks/use-resend-timer'
import { ActionStatus } from '@/core/types/enums/action-status.enum'
import { Button } from '@/core/ui/atoms/button'
import { VerificationInput } from '@/core/ui/forms/verification-input'
import { verifyPhoneAction } from '@/features/auth/actions/verify-account.server.action'
import { useTranslations } from 'next-intl'
import { useActionState, useCallback, useEffect, useRef } from 'react'
import { useSendVerificationCode } from '../hooks/use-send-verification-code'
import { useSendVerificationCodeStore } from '../store/phone-login-store'

type Props = {
  onClose?: () => void
}

export const VerifyPhone: React.FC<Props> = ({ onClose }) => {
  const t = useTranslations()
  const formRef = useRef<HTMLFormElement>(null)
  const { phoneNumber, verificationId, reset } = useSendVerificationCodeStore()
  const { sendCode, isPending: resendCodePending } = useSendVerificationCode()

  const { remainingTime, canRequestResend, startTimer } =
    useResendTimer(phoneNumber)

  const [state, formAction, isPending] = useActionState(verifyPhoneAction, null)

  useEffect(() => {
    if (state?.status === ActionStatus.SUCCESS) {
      reset()
      onClose?.()
    }
  }, [state?.status, reset, onClose])

  useEffect(() => {
    if (!(phoneNumber && verificationId)) {
      onClose?.()
    }
  }, [phoneNumber, verificationId, onClose])

  const handleVerificationComplete = useCallback(() => {
    formRef.current?.requestSubmit()
  }, [])

  const handleGetSms = async () => {
    if (!canRequestResend) return
    await sendCode(phoneNumber)
    startTimer()
  }

  return (
    <div className='text-navy' aria-busy={isPending}>
      <h1 className='my-4 text-xl font-medium'>{t('code.verifyTitle')}</h1>
      <p className='text-2xl font-semibold'>{t('signin.welcome')}</p>
      <p className='my-3'>{t('code.sentMsg')}</p>

      <form
        action={formAction}
        ref={formRef}
        className='flex flex-col items-stretch gap-1'
        aria-describedby={state?.error ? 'error-message' : undefined}
      >
        <input type='hidden' name='phoneNumber' value={phoneNumber} />
        <input type='hidden' name='verificationId' value={verificationId} />

        <label htmlFor='code' className='text-sm font-medium'>
          {t('forms.codeLabel')}
        </label>

        <VerificationInput
           
          autoFocus
          isRequired
          isInvalid={Boolean(state?.error)}
          errorMessage={state?.errors?.code?.[0] || state?.error}
          name='code'
          isDisabled={isPending}
          onComplete={handleVerificationComplete}
          aria-describedby={state?.error ? 'error-message' : undefined}
        />

        <div className='mt-4 flex justify-between gap-2'>
          <Button
            onClick={onClose}
            variant='outlined'
            className='min-w-36 flex-1'
            disabled={isPending}
            type='button'
          >
            {t('actions.close')}
          </Button>
          <Button
            type='submit'
            color='navy'
            className='min-w-36 flex-1'
            loading={isPending}
            disabled={isPending}
          >
            {t('actions.verify')}
          </Button>
        </div>
      </form>

      <div className='my-4 flex items-center gap-2'>
        <p>{t('code.noCodeQ')}</p>
        <Button
          type='button'
          color='sky-blue'
          variant='text'
          className='p-0'
          onClick={handleGetSms}
          disabled={!canRequestResend || resendCodePending}
          loading={resendCodePending}
        >
          {t('actions.getSMS')}
        </Button>
      </div>
      {!canRequestResend && (
        <p className='text-grey font-medium'>
          {t('code.waitMsg', {
            time: `${String(Math.floor(remainingTime / 60)).padStart(2, '0')}:${String(remainingTime % 60).padStart(2, '0')}`,
          })}
        </p>
      )}
    </div>
  )
}
